
package diana.soleil.design.iraniancanadienbazzar.feature.dashboard

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ListingMetricsRepository
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ChartSeries
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.SectionHeader
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.TrendLineChart
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingCounters
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_delta_down
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_delta_up
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_period_month
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_period_week
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_stat_shares
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_subtitle
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_title
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.analytics_trends_section
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.dash_stat_leads
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.dash_stat_views
import kotlin.math.roundToInt
import kotlin.time.Clock
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** Selectable trend window. No "Day" option: buckets are daily, so a 1-day trend is a single dot. */
enum class AnalyticsPeriod(val days: Int) { Week(7), Month(30) }

/** One stat tile's worth of data: this window's total + % change vs the previous equal window. */
data class AnalyticsStat(
    val total: Long = 0,
    /** Percent change vs the previous window; null when the previous window is 0 (no honest baseline). */
    val deltaPercent: Int? = null,
)

data class ListingAnalyticsUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val businessName: String = "",
    val period: AnalyticsPeriod = AnalyticsPeriod.Month,
    val views: AnalyticsStat = AnalyticsStat(),
    val leads: AnalyticsStat = AnalyticsStat(),
    val shares: AnalyticsStat = AnalyticsStat(),
    /** Per-day values for the current window (oldest → newest), zero-filled for silent days. */
    val viewsSeries: List<Float> = emptyList(),
    val leadsSeries: List<Float> = emptyList(),
) {
    val hasActivity: Boolean get() = views.total > 0 || leads.total > 0 || shares.total > 0
}

/**
 * Listing Analytics (Stitch `26dc5711`) off the daily `metrics/daily-YYYYMMDD` buckets. Fetches
 * 2× the window so deltas compare against the previous period. **Honest subset of the design**:
 * the mock's Top Categories / Audience Location / Recent Interactions need per-event geo data we
 * don't collect — omitted, never faked.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class ListingAnalyticsViewModel(
    businessRepository: BusinessRepository,
    listingMetrics: ListingMetricsRepository,
    private val businessId: String,
) : ViewModel() {

    private val period = MutableStateFlow(AnalyticsPeriod.Month)
    private val businessName = MutableStateFlow("")

    init {
        viewModelScope.launch {
            businessName.value =
                (businessRepository.getBusiness(businessId) as? DataResult.Success)?.data?.name.orEmpty()
        }
    }

    val state: StateFlow<ListingAnalyticsUiState> = period
        .flatMapLatest { p ->
            val today = todayUtc()
            val windowStart = today.minus(DatePeriod(days = p.days - 1))
            val previousStart = windowStart.minus(DatePeriod(days = p.days))
            listingMetrics.observeDailyCounters(businessId, previousStart.toDayString())
                .map { result -> p to result }
        }
        .combine(businessName) { (p, result), name ->
            when (result) {
                is DataResult.Failure -> ListingAnalyticsUiState(loading = false, error = true, period = p)
                is DataResult.Success -> buildState(p, name, result.data)
            }
        }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ListingAnalyticsUiState())

    fun selectPeriod(p: AnalyticsPeriod) {
        period.value = p
    }

    private fun buildState(
        p: AnalyticsPeriod,
        name: String,
        buckets: List<DailyListingCounters>,
    ): ListingAnalyticsUiState {
        val today = todayUtc()
        val byDay = buckets.associateBy { it.day }
        // Oldest → newest, zero-filled: silent days have no bucket doc but must appear in the trend.
        val currentDays = (p.days - 1 downTo 0).map { today.minus(DatePeriod(days = it)).toDayString() }
        val previousDays = (2 * p.days - 1 downTo p.days).map { today.minus(DatePeriod(days = it)).toDayString() }
        val current = currentDays.map { byDay[it]?.counters ?: ListingCounters() }
        val previous = previousDays.map { byDay[it]?.counters ?: ListingCounters() }
        return ListingAnalyticsUiState(
            loading = false,
            businessName = name,
            period = p,
            views = stat(current.sumOf { it.views }, previous.sumOf { it.views }),
            leads = stat(current.sumOf { it.leads }, previous.sumOf { it.leads }),
            shares = stat(current.sumOf { it.shares }, previous.sumOf { it.shares }),
            viewsSeries = current.map { it.views.toFloat() },
            leadsSeries = current.map { it.leads.toFloat() },
        )
    }

    private fun stat(current: Long, previous: Long) = AnalyticsStat(
        total = current,
        deltaPercent = if (previous > 0) (((current - previous) * 100f) / previous).roundToInt() else null,
    )

    private fun todayUtc(): LocalDate = Clock.System.now().toLocalDateTime(TimeZone.UTC).date

    /** `YYYYMMDD`, matching the bucket doc ids/`day` field written by recordListingMetric. */
    private fun LocalDate.toDayString(): String =
        "${year.toString().padStart(4, '0')}${month.number.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}"
}

@Composable
fun ListingAnalyticsRoute(
    businessId: String,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: ListingAnalyticsViewModel = koinViewModel(parameters = { parametersOf(businessId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    ListingAnalyticsScreen(
        state = state,
        onBack = onBack,
        onSelectPeriod = viewModel::selectPeriod,
        modifier = modifier,
    )
}

/** Listing Analytics (Stitch `26dc5711`, honest subset — see ViewModel kdoc). Tokens + resources only. */
@Composable
fun ListingAnalyticsScreen(
    state: ListingAnalyticsUiState,
    onBack: () -> Unit,
    onSelectPeriod: (AnalyticsPeriod) -> Unit,
    modifier: Modifier = Modifier,
) {
    LazyColumn(
        modifier = modifier.background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.contentMaxWidth).fillMaxHeight().statusBarsPadding().navigationBarsPadding(),
        contentPadding = PaddingValues(
            start = AppTheme.spacing.lg,
            end = AppTheme.spacing.lg,
            top = AppTheme.spacing.md,
            bottom = AppTheme.spacing.xl,
        ),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        item {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                AppIconButton(
                    iconName = "arrow_back",
                    contentDescription = stringResource(Res.string.analytics_back_a11y),
                    onClick = onBack,
                    tint = AppTheme.colors.onSurface,
                )
                Column {
                    Text(
                        text = stringResource(Res.string.analytics_title),
                        style = AppTheme.typography.headlineLg,
                        color = AppTheme.colors.onSurface,
                    )
                    if (state.businessName.isNotBlank()) {
                        Text(
                            text = stringResource(Res.string.analytics_subtitle, state.businessName, state.period.days),
                            style = AppTheme.typography.bodySm,
                            color = AppTheme.colors.onSurfaceVariant,
                        )
                    }
                }
            }
        }

        if (state.loading) {
            item { LoadingState() }
            return@LazyColumn
        }
        if (state.error) {
            item {
                EmptyState(
                    title = stringResource(Res.string.analytics_empty_title),
                    description = stringResource(Res.string.analytics_empty_desc),
                )
            }
            return@LazyColumn
        }

        item {
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                AppChip(
                    label = stringResource(Res.string.analytics_period_week),
                    selected = state.period == AnalyticsPeriod.Week,
                    onClick = { onSelectPeriod(AnalyticsPeriod.Week) },
                )
                AppChip(
                    label = stringResource(Res.string.analytics_period_month),
                    selected = state.period == AnalyticsPeriod.Month,
                    onClick = { onSelectPeriod(AnalyticsPeriod.Month) },
                )
            }
        }

        item {
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                AnalyticsStatCard(
                    stat = state.views,
                    label = stringResource(Res.string.dash_stat_views),
                    icon = "visibility",
                    modifier = Modifier.weight(1f),
                )
                AnalyticsStatCard(
                    stat = state.leads,
                    label = stringResource(Res.string.dash_stat_leads),
                    icon = "call",
                    modifier = Modifier.weight(1f),
                )
                AnalyticsStatCard(
                    stat = state.shares,
                    label = stringResource(Res.string.analytics_stat_shares),
                    icon = "share",
                    modifier = Modifier.weight(1f),
                )
            }
        }

        if (!state.hasActivity) {
            item {
                EmptyState(
                    title = stringResource(Res.string.analytics_empty_title),
                    description = stringResource(Res.string.analytics_empty_desc),
                )
            }
            return@LazyColumn
        }

        item { SectionHeader(title = stringResource(Res.string.analytics_trends_section)) }
        item {
            AppCard(modifier = Modifier.fillMaxWidth()) {
                TrendLineChart(
                    series = listOf(
                        ChartSeries(
                            label = stringResource(Res.string.dash_stat_views),
                            values = state.viewsSeries,
                            color = AppTheme.colors.primary,
                        ),
                        ChartSeries(
                            label = stringResource(Res.string.dash_stat_leads),
                            values = state.leadsSeries,
                            color = AppTheme.colors.secondary,
                        ),
                    ),
                    modifier = Modifier.fillMaxWidth().padding(AppTheme.spacing.lg),
                )
            }
        }
    }
}

/** Stat tile with the Stitch delta badge (trending icon + %). Delta hidden without a real baseline. */
@Composable
private fun AnalyticsStatCard(
    stat: AnalyticsStat,
    label: String,
    icon: String,
    modifier: Modifier = Modifier,
) {
    AppCard(modifier = modifier) {
        Column(
            modifier = Modifier.padding(AppTheme.spacing.md),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                AppIcon(name = icon, size = AppTheme.sizes.iconMd, tint = AppTheme.colors.primary)
                val delta = stat.deltaPercent
                if (delta != null) {
                    val up = delta >= 0
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                    ) {
                        AppIcon(
                            name = if (up) "trending_up" else "trending_down",
                            size = AppTheme.sizes.iconSm,
                            tint = if (up) AppTheme.colors.success else AppTheme.colors.error,
                        )
                        Text(
                            text = if (up) {
                                stringResource(Res.string.analytics_delta_up, delta)
                            } else {
                                stringResource(Res.string.analytics_delta_down, -delta)
                            },
                            style = AppTheme.typography.labelSm,
                            color = if (up) AppTheme.colors.success else AppTheme.colors.error,
                        )
                    }
                }
            }
            Text(
                text = stat.total.toString(),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onSurface,
            )
            Text(text = label, style = AppTheme.typography.labelSm, color = AppTheme.colors.onSurfaceVariant)
        }
    }
}
